<template>
  <div class="bulk-upload-popup-wrapper">
    <div class="bulk-upload-popup-backdrop">
      <div class="bulk-upload-popup">
        <div
          class="bulk-upload-popup-close"
          :title="$t('bulkUploadPopup.close')"
          @click="close"
        >
          <i class="icon-cross" />
        </div>
        <div
          class="bulk-upload-popup-text"
          v-html="$t(`bulkUploadPopup.${type}.${popupType}.text`)"
        />
        <!-- {{ $t('bulkUploadPopup.synonyms.add.okMessage') }} -->
        <!-- {{ $t('bulkUploadPopup.synonyms.add.text') }} -->
        <!-- {{ $t('bulkUploadPopup.synonyms.replace.okMessage') }} -->
        <!-- {{ $t('bulkUploadPopup.synonyms.replace.text') }} -->
        <div v-if="!fileData">
          <file-pond
            ref="pond"
            v-bind="filepondOptions"
          />
          <!-- {{ $t('bulkUploadPopup.filepond.labelIdle') }} -->
          <!-- {{ $t('bulkUploadPopup.filepond.fileValidateTypeLabelExpectedTypes') }} -->
          <div class="bulk-upload-popup-buttons">
            <button
              type="button"
              class="bulk-upload-popup-button-structure-info button"
              @click="setIsStructureInfoVisible(!isStructureInfoVisible)"
            >
              <span class="button__content">{{ $t('bulkUploadPopup.structureButton') }}</span>
            </button>
            <button
              type="button"
              class="bulk-upload-popup-button-structure-browse button button--cyan2"
              @click="openFileBrowser"
            >
              <span class="button__content">{{ $t('bulkUploadPopup.uploadButton') }}</span>
            </button>
          </div>
          <div
            v-show="isStructureInfoVisible"
            class="bulk-upload-popup-structure-info"
          >
            <div
              class="bulk-upload-popup-structure-info-wrap"
            >
              <div
                class="bulk-upload-popup-structure-info-close"
                :title="$t('bulkUploadPopup.close')"
                @click="setIsStructureInfoVisible(false)"
              >
                <i class="icon-cross" />
              </div>
              <i class="icon-pin-info" />
              <div
                class="bulk-upload-popup-structure-info-content"
                v-html="$t(`bulkUploadPopup.${type}.${popupType}.structureInfo`)"
              />
            <!-- {{ $t('bulkUploadPopup.synonyms.add.structureInfo') }} -->
            <!-- {{ $t('bulkUploadPopup.synonyms.replace.structureInfo') }} -->
            </div>
          </div>
        </div>
        <div v-else>
          <div
            v-if="fileData.list && fileData.list.length"
            class="bulk-upload-popup-file"
          >
            <div class="bulk-upload-popup-file-name">
              <strong>{{ $t('bulkUploadPopup.file.name') }}</strong> {{ fileData.name }}
            </div>
            <div class="bulk-upload-popup-file-items">
              <strong>{{ $t('bulkUploadPopup.file.items') }}</strong> {{ fileData.list.length }}
            </div>
          </div>
          <div class="table-data">
            <div class="table-data-scroll">
              <table v-if="fileData.list && fileData.list.length">
                <tr v-if="fileData.headers">
                  <th
                    v-for="(header, headerInd) in fileData.headers"
                    :key="`${header}-${headerInd}`"
                  >
                    {{ header }}
                  </th>
                </tr>
                <tr
                  v-for="(row, rowInd) in fileData.list.slice(0, maxRowsInPreview)"
                  :key="rowInd"
                >
                  <td
                    v-for="(cell, cellInd) in row"
                    :key="cellInd"
                  >
                    {{ cell }}
                  </td>
                </tr>
              </table>
            </div>
            <div
              v-if="fileData.list.length > maxRowsInPreview"
              class="table-data-bottom"
            >
              {{ $t('bulkUploadPopup.previewLimitedInfo', {rows: maxRowsInPreview}) }}
            </div>
          </div>
          <div class="bulk-upload-popup-apply-buttons">
            <button
              type="button"
              class="bulk-upload-popup-button-save button button--cyan2"
              @click="save"
            >
              <span class="button__content">{{ $t('bulkUploadPopup.saveButton') }}</span>
            </button>
            <button
              type="button"
              class="bulk-upload-popup-button-cancel button button--error"
              @click="close"
            >
              <span class="button__content">{{ $t('bulkUploadPopup.cancelButton') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueFilepond from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { CSVToArray, transformCSVArray } from '../../vue-utils/csv';

const FilePond = VueFilepond(FilePondPluginFileValidateType);

export default {
    components: {
        FilePond,
    },
    props: {
        siteId: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        popupType: {
            type: String,
            required: true,
        },
        maxRowsInPreview: {
            type: Number,
            default: 100,
        },
    },
    data() {
        return {
            disableSubmit: false,
            file: null,
            fileData: null,
            filepondOptions: {
                server: {
                    process: (
                        fieldName, file, metadata, load,
                        // _error, _progress, _abort, _transfer, _options
                    ) => {
                        load();
                        setTimeout(() => {
                            this.loadFile(file);
                        }, 0); // 1500 for UX
                    },
                },
                acceptedFileTypes: 'text/csv',
            },
            isStructureInfoVisible: false,
        };
    },
    computed: {
        filepondLabels() {
            // https://pqina.nl/filepond/docs/api/instance/properties/#labels

            const labelsFromFallbackLang = this.$i18n.messages[this.$i18n.fallbackLocale]
                .filepond || {};
            const labelsFromLang = this.$i18n.messages[this.$i18n.locale]
                .filepond || {};

            const customLabelsFromFallbackLang = this.$i18n.messages[this.$i18n.fallbackLocale]
                .bulkUploadPopup?.filepond || {};
            const customLabelsFromLang = this.$i18n.messages[this.$i18n.locale]
                .bulkUploadPopup?.filepond || {};

            const customLabels = {};

            return {
                ...labelsFromFallbackLang,
                ...labelsFromLang,
                ...customLabelsFromFallbackLang,
                ...customLabelsFromLang,
                ...customLabels,
            };
        },
    },
    created() {
        this.filepondOptions = {
            ...this.filepondOptions,
            ...this.filepondLabels,
        };
    },
    methods: {
        openFileBrowser() {
            this.$refs.pond.browse();
        },
        close() {
            this.$emit('close');
        },
        setIsStructureInfoVisible(val) {
            this.isStructureInfoVisible = val;
        },
        async save() {
            if (this.disableSubmit) {
                return;
            }
            this.disableSubmit = true;
            try {
                await this.$api.bulkCSVUpload[this.type]({
                    siteId: this.siteId,
                    items: this.fileData.list,
                    type: this.popupType,
                });
                this.disableSubmit = false;
                this.close();
                this.$notification(this.$t(`bulkUploadPopup.${this.type}.${this.popupType}.okMessage`), 'good');
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } catch (error) {
                this.disableSubmit = false;
                this.$api.$handleError(error, this);
            }
        },
        loadFile(file) {
            this.file = file;
            const reader = new FileReader();

            reader.readAsText(file);

            reader.onload = () => {
                let result = CSVToArray(reader.result).data || [];
                result = transformCSVArray(result);

                let maxColumns = 0;
                for (let i = 0; i < result.length; i += 1) {
                    if (result[i].length > maxColumns) {
                        maxColumns = result[i].length;
                    }
                }
                const headersFromLang = this.$t('bulkUploadPopup.synonyms.headers').split(';');
                const headers = [];
                for (let i = 0; i < maxColumns; i += 1) {
                    headers.push(headersFromLang[i] || headersFromLang[headersFromLang.length - 1]);
                }
                /*
                for (let i = 0; i < 150; i += 1) {
                    result.push(result[0]);
                }
                */
                this.fileData = {
                    list: result,
                    name: file.name,
                    headers,
                };
            };

            reader.onerror = () => {
                this.$notification(this.$t('errors.default'), 'bad');
                this.close();
            };
        },
    },
};
</script>

<style lang="stylus" scoped>

    ::v-deep .warning-text {
        color: $colorError;
    }

    ::v-deep h3 {
        font-size: 16px;
        padding-bottom: 15px;
    }

    ::v-deep table {
        width: 100%;

        tr {
            &:not(:last-child) {
                border-bottom: 1px dashed #b8bcc5;
            }

            th {
                font-weight: bold;
            }

            td, th {

                text-align: left;
                padding: 10px 30px 10px 0px;

                &:not(:first-child) {
                    border-left: 1px dashed #b8bcc5;
                    padding-left: 30px;
                }
            }
        }
    }

    .bulk-upload-popup-wrapper {
        width: 100%;
        height: 100vh;
        position: fixed;
        top: 0;
        left: 0;
        z-index: 2;

        .bulk-upload-popup-backdrop {
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.3);
            position: absolute;
            top: 0;
            left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .bulk-upload-popup
        {
            background: #fff;
            padding: 20px;
            border-radius: 5px;
            box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
            width: 80%;
            max-width: 570px;
            position: relative;
        }
    }

    .bulk-upload-popup-file {
        display: flex;
        padding-bottom: 10px;

        .bulk-upload-popup-file-name {
            margin-right: 30px;
        }
    }

    .bulk-upload-popup-close {
        font-size: 16px;
        cursor: pointer;
        color: #000;
        position: absolute;
        top: 20px;
        right: 20px;
    }

     .table-data {
        background: #f5f6fb;
        border: 1px dashed #b8bcc5;
        border-radius: 5px;
        min-height: 100px;
        padding: 5px;
        color: #7C828F;
        font-size: 12px;

        .table-data-scroll {
            padding: 5px 15px;
            overflow: auto;
            max-height: 300px;
        }

        .table-data-bottom {
            text-align: center;
            font-weight: bold;
            padding: 15px;
        }

    }

    .bulk-upload-popup-buttons {
        display: flex;
        justify-content: space-between;

        button {
            width: 48%;
            margin-top: 20px;
        }
    }

    .bulk-upload-popup-apply-buttons {
        text-align: center;

        .bulk-upload-popup-button-save {
            width: 100%;
        }

        button {
            margin-top : 20px;
        }
    }

    ::v-deep .bulk-upload-popup-structure-info-wrap {
        background: #FEFAE4;
        padding: 20px 35px 10px;
        position: relative;
        font-size: 12px;
        line-height: 1.6em;
        color: #000;
        margin-top: 20px;

        .icon-pin-info {
            font-size: 20px;
            color: #4d89d1;
            position: absolute;
            top: 20px;
            left: 10px;
        }

        .bulk-upload-popup-structure-info-close {
            position: absolute;
            top: 10px;
            right: 10px;
            font-size: 15px;
            cursor: pointer;
        }

        h3 {
            font-size: 14px;
            font-weight: bold;
            padding-bottom: 10px;
        }
    }
</style>
